import { RiCheckLine } from '@remixicon/react';
import { forwardRef } from 'react';
import { Menu, MenuItem, MenuItemProps, MenuProps, Popover, PopoverProps, Separator } from 'react-aria-components';
import { cn } from './utils';

export interface AgMenuPopoverProps extends Omit<PopoverProps, 'children'> {
  children: React.ReactNode;
}

export const AgMenuPopover = forwardRef<HTMLDivElement, AgMenuPopoverProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Popover
        ref={ref}
        className={cn(
          'min-w-55 overflow-hidden rounded-md bg-white border border-neutral-5 shadow-sm p-1.5 outline-none',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
      </Popover>
    );
  }
);

AgMenuPopover.displayName = 'AgMenuPopover';

export interface AgMenuProps<T extends object> extends MenuProps<T> {
  children: React.ReactNode;
}

export function AgMenu<T extends object>({ children, className, ...props }: AgMenuProps<T>) {
  return (
    <Menu className={cn('outline-none max-h-[300px] overflow-auto', className)} {...props}>
      {children}
    </Menu>
  );
}

export interface AgMenuItemProps extends MenuItemProps {
  children: React.ReactNode;
  /** Show a checkmark when selected */
  showCheck?: boolean;
  /** Destructive/danger styling */
  destructive?: boolean;
}

export const AgMenuItem = forwardRef<HTMLDivElement, AgMenuItemProps>(
  ({ children, className, showCheck, destructive, ...props }, ref) => {
    return (
      <MenuItem
        ref={ref}
        className={cn(
          'relative flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer outline-none text-14',
          destructive
            ? 'text-red-text hover:bg-red-bg data-focused:bg-red-bg'
            : 'text-neutral-text hover:bg-[rgba(10,15,41,0.04)] data-focused:bg-[rgba(10,15,41,0.04)]',
          'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
          'data-selected:bg-brand-alpha-1',
          className
        )}
        {...props}
      >
        {({ isSelected }) => (
          <>
            <span className="flex-1">{children}</span>
            {showCheck && isSelected && (
              <RiCheckLine className="h-4 w-4 shrink-0 text-neutral-alpha-8" aria-hidden="true" />
            )}
          </>
        )}
      </MenuItem>
    );
  }
);

AgMenuItem.displayName = 'AgMenuItem';

export const AgMenuSeparator = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <Separator ref={ref} className={cn('my-1 h-px bg-neutral-5', className)} {...props} />;
  }
);

AgMenuSeparator.displayName = 'AgMenuSeparator';
